import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import ExplorerShell from "../components/explorer/ExplorerShell";
import {
  KafkaEventExplorerCard,
  KafkaEventExplorerListRow,
} from "../components/explorer/KafkaEventExplorerViews";
import { useExplorerUrlState } from "../hooks/useExplorerUrlState";
import { apiService } from "../api/services/apiService";
import type { KafkaEventTopic } from "../types";
import { KafkaSchemaFormat } from "../types";
import {
  compareExplorerValues,
  isVisibleToExplorer,
  matchesExplorerSearch,
  paginate,
  EXPLORER_PAGE_SIZE_CARD,
  EXPLORER_PAGE_SIZE_LIST,
} from "../utils/explorerUtils";

const SORT_OPTIONS = [
  { id: "name", label: "Name" },
  { id: "environment", label: "Environment" },
  { id: "schemaFormat", label: "Schema format" },
  { id: "status", label: "Status" },
];

const KafkaEventExplorerPage: React.FC = () => {
  const state = useExplorerUrlState({ defaultSort: "name" });
  const [topics, setTopics] = useState<KafkaEventTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [envFilter, setEnvFilter] = useState("");
  const [formatFilter, setFormatFilter] = useState<KafkaSchemaFormat | "">("");

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiService.getKafkaEventTopics();
      setTopics(list.filter((t) => isVisibleToExplorer(t.status)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load Kafka event topics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const processed = useMemo(() => {
    let list = topics;

    if (state.statuses.length) {
      list = list.filter((t) =>
        state.statuses.includes(t.status as (typeof state.statuses)[number]),
      );
    }

    if (envFilter.trim()) {
      const env = envFilter.toLowerCase();
      list = list.filter((t) => t.environment.toLowerCase().includes(env));
    }

    if (formatFilter) {
      list = list.filter((t) => t.schemaFormat === formatFilter);
    }

    list = list.filter((t) =>
      matchesExplorerSearch(state.debouncedQ, [
        t.name,
        t.environment,
        t.purpose,
        t.description,
        t.schemaSubject,
        t.schemaFormat,
      ]),
    );

    list = [...list].sort((a, b) => {
      let av: string = "";
      let bv: string = "";
      switch (state.sort) {
        case "environment":
          av = a.environment;
          bv = b.environment;
          break;
        case "schemaFormat":
          av = a.schemaFormat;
          bv = b.schemaFormat;
          break;
        case "status":
          av = a.status;
          bv = b.status;
          break;
        default:
          av = a.name;
          bv = b.name;
      }
      return compareExplorerValues(av, bv, state.sortDir);
    });

    return list;
  }, [topics, state.debouncedQ, state.sort, state.sortDir, state.statuses, envFilter, formatFilter]);

  const pageSize =
    state.layout === "list"
      ? EXPLORER_PAGE_SIZE_LIST
      : EXPLORER_PAGE_SIZE_CARD;
  const paginated = paginate(processed, state.page, pageSize);

  return (
    <ExplorerShell
      pageTitle="Kafka Event Explorer"
      pageDescription="Browse registered Kafka topics and schemas. Read-only catalog for all authenticated users."
      loading={loading}
      error={error}
      items={paginated}
      totalFiltered={processed.length}
      state={state}
      sortOptions={SORT_OPTIONS}
      emptyMessage="No Kafka event topics are available to explore."
      getItemKey={(t) => t.uuid}
      renderCard={(t) => <KafkaEventExplorerCard topic={t} />}
      renderListRow={(t) => <KafkaEventExplorerListRow topic={t} />}
      onClearAllExtra={() => {
        setEnvFilter("");
        setFormatFilter("");
      }}
      hasExtraFilters={!!envFilter.trim() || !!formatFilter}
      filterSidebarExtra={
        <>
          <TextField
            fullWidth
            size="small"
            label="Environment contains"
            value={envFilter}
            onChange={(e) => {
              setEnvFilter(e.target.value);
              state.setPage(1);
            }}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            select
            size="small"
            label="Schema format"
            value={formatFilter}
            onChange={(e) => {
              setFormatFilter(e.target.value as KafkaSchemaFormat | "");
              state.setPage(1);
            }}
            sx={{ mt: 2 }}
          >
            <MenuItem value="">All</MenuItem>
            {Object.values(KafkaSchemaFormat).map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </TextField>
        </>
      }
    />
  );
};

export default KafkaEventExplorerPage;
